import { Image } from 'antd';
import styled, { css } from 'styled-components';
import { Button } from 'components/Button';
import { AddIcon } from 'icons/AddIcon';
import { Eye } from 'icons/Eye';
import { FileUpload } from 'icons/UploadFileIcon';
import { TButtonSize } from 'interfaces';

export const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 20px;
`;

export const TopWrapper = styled.div<{ $multiSelect?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  justify-content: space-between;

  ${({ $multiSelect }) =>
    $multiSelect &&
    css`
      flex-direction: row;
      align-items: flex-start;
      justify-content: space-between;
    `}
`;

export const UploadButtonWrapper = styled.div<{
  $size: TButtonSize;
}>`
  display: flex;
  height: 132px;
  min-width: 132px;
  padding: 12px;
  max-width: 100%;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  border-radius: 14px;
  background: var(--v3-black-2);
  transition: background 0.2s ease-in-out;
  cursor: pointer;

  ${({ $size }) =>
    $size === 'normal' &&
    css`
      aspect-ratio: 1;
    `}
  ${({ $size }) =>
    $size === 'wider' &&
    css`
      aspect-ratio: 1.5;
    `}
  ${({ $size }) =>
    $size === 'wide' &&
    css`
      aspect-ratio: 2;
    `}
  ${({ $size }) =>
    $size === 'fullWidth' &&
    css`
      width: 100%;
    `}

  &:hover {
    background: var(--v3-black-15);
  }
`;

export const UploadIcon = styled(AddIcon)`
  &&& {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 28px;
    border-radius: 50%;
  }
`;

export const UploadTitle = styled.div`
  color: var(--v3-text-color);
  text-align: center;
  font-size: 12px;
  font-weight: 500;
  line-height: 14px;
  letter-spacing: -0.24px;
`;

export const DescriptionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-self: stretch;
  color: var(--v3-text-color-secondary);
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: -0.24px;
  max-width: 70%;
  white-space: pre-line;
`;

export const ImageContainer = styled.div<{
  $size: TButtonSize;
}>`
  position: relative;
  display: flex;
  height: 132px;
  min-width: 132px;
  max-width: 100%;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  border-radius: 14px;
  overflow: hidden;

  ${({ $size }) =>
    $size === 'normal' &&
    css`
      aspect-ratio: 1;
    `}
  ${({ $size }) =>
    $size === 'wider' &&
    css`
      aspect-ratio: 1.5;
    `}
  ${({ $size }) =>
    $size === 'wide' &&
    css`
      aspect-ratio: 2;
    `}
  ${({ $size }) =>
    $size === 'fullWidth' &&
    css`
      width: 100%;
    `}
  
  &:hover .overlay {
    opacity: 1;
  }
`;

export const FileImageSingle = styled(Image)`
  &&& {
    border-radius: 8px;

    &.ant-image-img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
`;

export const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 0;
  transition: opacity 0.2s ease-in-out;
  backdrop-filter: blur(0.5px);
  background: rgba(0, 0, 0, 0.2);
  gap: 4px;
`;

export const PreviewIcon = styled(Eye)<{ $darkTheme?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 4px;
  font-size: 16px;
  ${props =>
    props.$darkTheme
      ? css`
          border-radius: 8px;
          background: var(--v3-black-2);
          color: var(--v3-black-85);
        `
      : css`
          border-radius: 50%;
          background: color-mix(in srgb, var(--v3-black-2) 80%, transparent);
          color: var(--v3-black-85);
        `}
`;

export const UploadEditIcon = styled(FileUpload)`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: color-mix(in srgb, var(--v3-black-2) 80%, transparent);
  color: var(--v3-black-85);
  font-size: 16px;
  padding: 4px;
  cursor: pointer;
`;

export const UploadMultipleBtn = styled(Button)`
  &&& {
    display: flex;
    padding: 0 16px;
    justify-content: center;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
    border-radius: 12px;
    background: var(--v3-black-4);
    border: unset;
  }
` as typeof Button;

export const ButtonText = styled.div`
  display: flex;
  padding: 14px 0;
  align-items: flex-start;
  gap: 10px;
  color: var(--v3-black-100);
  text-align: center;
  font-size: 14px;
  font-weight: 500;
  line-height: 16px;
  letter-spacing: -0.28px;
`;

export const ListWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  width: 100%;
  gap: 12px;
`;

export const UploadEditWrapper = styled.div`
  display: flex;
`;

export const FileName = styled.div`
  color: var(--v3-text-color-secondary);
  text-align: center;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: -0.14px;
  word-break: break-all;
  margin: 0 12px;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;
